// Placement footprints for windows that grow on their own.
//
// Most windows keep the size placement gave them until the person resizes
// them. The NeuralCanvas conversation does not: its window is the bare sphere
// while the transcript is closed and grows to fit the transcript whenever it
// opens. Placement that reserved only the current size let new and restored
// windows open in the transcript's path, and the transcript then opened on top
// of them. Placement therefore treats such a window as its full footprint,
// both when it places other windows around it and when it places the window
// itself. The quadtree keeps each window's real size; footprints are derived
// from the component list whenever a placement runs, so they never drift.

import { sphereConversationFootprint } from "@smartmanager/shell-ui/features/visualization/sphereConversation";
import { Rectangle, type componentsMapType } from "@/src/shared/contracts/app";
import { clampPositionToBounds, type LayoutPoint, type LayoutSize } from "@/src/state/appStore/layoutGeometry";
import type { HeldComponentHeights } from "@/src/state/appStore/heldHeights";

type FootprintSource = Pick<componentsMapType, "name" | "size"> & { position?: LayoutPoint };

/** Anything placement can test a candidate rectangle against. */
export type PlacementOccupancy = { overlapsAny(rect: Rectangle): boolean };

/** Whether a window's footprint can exceed its current size (see placementFootprint). */
export function growsOnItsOwn(component: Pick<componentsMapType, "name">, isMobileLayout: boolean): boolean {
  return !isMobileLayout && component.name === "NeuralCanvas";
}

/**
 * The area a window can come to cover without being moved or resized by the
 * person, at its position (or anywhere, without one). Never smaller than its
 * current size. The mobile layout keeps every window at its placed size.
 */
export function placementFootprint(
  component: FootprintSource,
  workspace: LayoutSize,
  isMobileLayout: boolean,
): LayoutSize {
  if (!growsOnItsOwn(component, isMobileLayout)) return component.size;
  const grown = sphereConversationFootprint(component.position?.x, workspace.width);
  if (grown.width <= component.size.width && grown.height <= component.size.height) return component.size;
  return {
    width: Math.max(component.size.width, grown.width),
    height: Math.max(component.size.height, grown.height),
  };
}

/**
 * `position` moved just enough for the window's footprint at that spot to lie
 * inside the workspace, the way the window itself is repaired once it grows.
 * The footprint depends on where the window sits, so the clamp repeats until
 * the position holds. For the conversation that takes two passes at most: the
 * clamp only moves it left within the same layout (beside or under the sphere).
 */
export function clampFootprintIntoWorkspace(
  component: Pick<componentsMapType, "name" | "size">,
  position: LayoutPoint,
  workspace: LayoutSize,
  isMobileLayout: boolean,
  margin: number,
): LayoutPoint {
  let clamped = clampPositionToBounds(position, component.size, workspace, margin);
  for (let pass = 0; pass < 3; pass += 1) {
    const footprint = placementFootprint({ ...component, position: clamped }, workspace, isMobileLayout);
    const next = clampPositionToBounds(clamped, footprint, workspace, margin);
    if (next.x === clamped.x && next.y === clamped.y) break;
    clamped = next;
  }
  return clamped;
}

/**
 * Occupancy rectangles (with `margin`) for the visible windows whose footprint
 * extends past their current size. `excludeId` leaves out the window being placed.
 *
 * A window folded to one line for a moment (a `held` entry with `growth`, see
 * heldHeights.ts) grows back to its held height when it opens, so it reserves
 * that height while folded. A window placed into the freed space would
 * otherwise end up under it. A fold that can last reserves nothing extra.
 */
export function growthReservations(
  components: componentsMapType[],
  workspace: LayoutSize,
  isMobileLayout: boolean,
  margin: number,
  excludeId?: string,
  held: HeldComponentHeights = {},
): Rectangle[] {
  const reservations: Rectangle[] = [];
  for (const component of components) {
    if (component.isMinimized || component.id === excludeId) continue;
    const hold = held[component.id];
    if (hold?.growth) {
      const position = isMobileLayout && component.mobilePosition ? component.mobilePosition : component.position;
      const size = isMobileLayout && component.mobileSize ? component.mobileSize : component.size;
      const heldHeight = isMobileLayout ? hold.mobileHeight ?? hold.height : hold.height;
      if (heldHeight <= size.height) continue;
      reservations.push(new Rectangle(
        position.x - margin,
        position.y - margin,
        size.width + margin * 2,
        heldHeight + margin * 2,
      ));
      continue;
    }
    const footprint = placementFootprint(component, workspace, isMobileLayout);
    if (footprint === component.size) continue;
    reservations.push(new Rectangle(
      component.position.x - margin,
      component.position.y - margin,
      footprint.width + margin * 2,
      footprint.height + margin * 2,
    ));
  }
  return reservations;
}

/** The quadtree's occupancy plus the reserved growth of windows that grow on their own. */
export function withGrowthReservations(
  occupancy: PlacementOccupancy,
  reservations: Rectangle[],
): PlacementOccupancy {
  if (reservations.length === 0) return occupancy;
  return {
    overlapsAny: (rect) =>
      occupancy.overlapsAny(rect) || reservations.some((reserved) => reserved.overlaps(rect)),
  };
}
